import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Observable, zip } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { TravelerTypes } from '@pax/entities/traveler-info';
import { TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { FormInputType } from '@forms/form-text-input/entities/input-type';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { PaxDocumentsPipe } from '@pax/pipes/pax-documents.pipe';
import { Travelers } from '@booking/entities/booking-arrangement';
import { BookingPaxTypeDocumentProps, BookingPaxDocumentValidations } from '@app/entities/booking-documents';
import { BookingDocumentPaxIdType } from '@app/entities/booking-pax-documents-id';

@Component({
  selector: 'app-passport-form',
  templateUrl: './passport-form.component.html',
  styleUrls: ['./passport-form.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PassportFormComponent implements OnInit {

  @Input() type: TravelerTypes;
  @Input() set form(form: UntypedFormGroup) {
    this._form = form;
  }

  @Output() setDocumentToPassport: EventEmitter<void> = new EventEmitter();

  public isSubmitted: boolean = false;
  public passportForm: UntypedFormGroup;
  public passportNumberSelector: string = TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER;
  public documentInputType: FormInputType = FormInputType.DOCUMENT;

  private _form: UntypedFormGroup;

  constructor(
    private formValidationService: FormValidationService,
    private paxDocumentsPipe: PaxDocumentsPipe,
  ) { }

  ngOnInit(): void {
    this.createPassportForm();
  }

  get documents$(): Observable<BookingPaxTypeDocumentProps[]> {
    return this.paxDocumentsPipe.transform(this.type, this.formValue?.Nationality);
  }

  get defaultDocumentId$(): Observable<number> {
    return this.documents$.pipe(map(documents => {
      const document = documents.find(doc => doc.TYPE === this.documentType);
      return document ? document.DOCUMENT_ID : null;
    }));
  }

  get defaultDocumentValidations$(): Observable<BookingPaxDocumentValidations> {
    return this.documents$.pipe(map(documents => {
      const document = documents.find(doc => doc.TYPE === this.documentType);
      return document ? document.VALIDATIONS : null;
    }));
  }

  get documentType(): BookingDocumentPaxIdType {
    return BookingDocumentPaxIdType.PAS;
  }

  get form(): UntypedFormGroup {
    return this._form;
  }

  private get formValue(): Travelers {
    return this.form?.value as Travelers;
  }

  public createPassportForm(): void {
    zip(
      this.defaultDocumentId$, this.defaultDocumentValidations$
    ).pipe(
      tap(([defaultDocumentId, validations]) => {
        this.passportForm = new UntypedFormGroup({
          [TRAVELER_FORM_INPUTS.DOCUMENT_ID]: new UntypedFormControl(
            defaultDocumentId, Validators.required),
          [TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER]: new UntypedFormControl(
            '', [
              ...this.formValidationService.documentValidator,
              Validators.pattern(validations?.REGEX),
              Validators.maxLength(validations?.MAX_LENGTH),
            ]),
        });
      })
    ).subscribe().unsubscribe();

  }

  public save(): void {
    if (this.form?.get(TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER)) {
      this.isSubmitted = true;
      if (!this.formValidationService.formInvalidControls(this.passportForm)) {
        this.defaultDocumentValidations$.pipe(
          take(1),
          tap(validations => {
            const values = this.passportForm.value;
            this.form?.patchValue({
              [TRAVELER_FORM_INPUTS.DOCUMENT_ID]: values[TRAVELER_FORM_INPUTS.DOCUMENT_ID],
              [TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER]: values[TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER]
            });
            this.formValidationService.setPaxDocumentValidators(this.documentType, this.form, validations);
            this.setDocumentToPassport.emit();
          })
        ).subscribe().unsubscribe();
      }
    }
  }

}
